import React, { createContext, useContext, useState, useEffect, useCallback } from 'react';
import { useAtomValue, useSetAtom } from 'jotai';
import { userAtom } from '@/store/atoms';
import { admin } from '@/api/endpoints/admin';
import { USER_PAGE_ID_TO_PATH, ORDERED_USER_PAGE_IDS } from '@/constant/role';
import { ADMIN_PAGE_ID_TO_PATH } from '@/constant/adminPages';
import { adminEmails } from '@/utils/format';

export interface UserRole {
  id: number;
  name: string;
  description: string;
  pages: string[];
}

interface RoleContextType {
  userRole: UserRole | null;
  allowedPages: string[];
  loading: boolean;
  hasPageAccess: (pageId: string) => boolean;
  getFirstAllowedUserPath: () => string;
  getFirstAllowedAdminPath: () => string;
  refreshRole: () => Promise<void>;
  syncRole: () => Promise<void>;
}

const RoleContext = createContext<RoleContextType>({
  userRole: null,
  allowedPages: [],
  loading: true,
  hasPageAccess: () => false,
  getFirstAllowedUserPath: () => '/user/dashboard',
  getFirstAllowedAdminPath: () => '/admin/dashboard',
  refreshRole: async () => {},
  syncRole: async () => {},
});

export const useUserRole = () => {
  const context = useContext(RoleContext);
  if (!context) {
    throw new Error('useUserRole must be used within RoleProvider');
  }
  return context;
};

export const RoleProvider: React.FC<{ children: React.ReactNode }> = ({ children }) => {
  const user = useAtomValue(userAtom);
  const setUser = useSetAtom(userAtom);
  const userId = user?.id;
  const userEmail = user?.email;
  const userRoleId = user?.roleId;
  const [userRole, setUserRole] = useState<UserRole | null>(() => {
    // Try to load from localStorage on init
    const cached = localStorage.getItem('userRole');
    return cached ? JSON.parse(cached) : null;
  });
  const [allowedPages, setAllowedPages] = useState<string[]>(() => {
    // Try to load from localStorage on init
    const cached = localStorage.getItem('userRole');
    return cached ? JSON.parse(cached).pages || [] : [];
  });
  // Only show loading if we don't have cached data
  const [loading, setLoading] = useState(() => {
    const cached = localStorage.getItem('userRole');
    const isLoading = !cached;
    return isLoading; // Only loading if no cache
  });

  const isAdminEmail = useCallback((email?: string | null) => {
    if (!email) return false;
    const normalizedEmail = email.trim().toLowerCase();
    return adminEmails.some((adminEmail) => adminEmail.trim().toLowerCase() === normalizedEmail);
  }, []);

  useEffect(() => {
    const fetchUserRole = async () => {
      
      if (!user?.id) {
        // User logged out - clear role cache
        localStorage.removeItem('userRole');
        localStorage.removeItem('userRoleId');
        setUserRole(null);
        setAllowedPages([]);
        setLoading(false);
        return;
      }

      // Check if we already have cached role data for this user
      const cachedRole = localStorage.getItem('userRole');
      const cachedUserId = localStorage.getItem('userRoleId');
      
      
      // Clear cache if userId changed (different user logged in)
      if (cachedUserId && cachedUserId !== user.id.toString()) {
        localStorage.removeItem('userRole');
        localStorage.removeItem('userRoleId');
      }
      
      if (cachedRole && cachedUserId === user.id.toString()) {
        // Use cached data - no API call needed
        const role = JSON.parse(cachedRole);
        setUserRole(role);
        setAllowedPages(role.pages || []);
        
        // Update userAtom with roleId
        if (user && user.roleId !== role.id) {
          setUser({ ...user, roleId: role.id });
        }
        
        setLoading(false);
        return;
      }

      // Need to fetch role data
      setLoading(true);
      
      try {
        let roleIdToUse = user.roleId || 1;
        if (isAdminEmail(user.email)) {
          const response = await admin.getUsers({
            search: user.email,
            limit: 1
          });

          let fetchedUser = null;
          if (Array.isArray(response.data)) {
            fetchedUser = response.data[0];
          } else if (response.data?.users) {
            fetchedUser = response.data.users[0];
          } else if (response.data?.data) {
            fetchedUser = response.data.data[0];
          }

          roleIdToUse = fetchedUser?.roleId || roleIdToUse;
        }
        
        // Fetch the specific role details
        const roleResponse = await admin.getRoleById(roleIdToUse);
        const role = roleResponse.data;
        
        const roleData = {
          id: role.id,
          name: role.name,
          description: role.description,
          pages: role.pages || [],
        };
        
        // Cache the role data
        localStorage.setItem('userRole', JSON.stringify(roleData));
        localStorage.setItem('userRoleId', user.id.toString());
        
        setUserRole(roleData);
        setAllowedPages(role.pages || []);
        
        // Update userAtom with roleId
        if (user) {
          setUser({ ...user, roleId: roleData.id });
        }
      } catch (error) {
        // On error, set default role 1
        try {
          const roleResponse = await admin.getRoleById(1);
          const role = roleResponse.data;
          const roleData = {
            id: role.id,
            name: role.name,
            description: role.description,
            pages: role.pages || [],
          };
          
          // Cache the fallback role
          localStorage.setItem('userRole', JSON.stringify(roleData));
          localStorage.setItem('userRoleId', user.id.toString());
          
          setUserRole(roleData);
          setAllowedPages(role.pages || []);
          
          // Update userAtom with roleId
          if (user) {
            setUser({ ...user, roleId: roleData.id });
          }
        } catch (fallbackError) {
          setUserRole(null);
          setAllowedPages([]);
        }
      } finally {
        setLoading(false);
      }
    };

    fetchUserRole();
  }, [user?.id, user?.email]);

  const hasPageAccess = (pageId: string): boolean => {
    const isFullAdminRole =
      Boolean(userRole?.name) && /full\s*admin/i.test(String(userRole?.name ?? ""));

    // Full Admin role should always have access to all pages (including newly added ones)
    if (isFullAdminRole) {
      return true;
    }

    // If loading or no role, allow dashboard only
    if (loading || !userRole || allowedPages.length === 0) {
      return pageId === 'dashboard';
    }
    
    // Check if the page is in the allowed pages list
    const hasAccess = allowedPages.includes(pageId);
    return hasAccess;
  };

  const getFirstAllowedUserPath = useCallback((): string => {
    if (loading || !userRole || allowedPages.length === 0) {
      return '/user/dashboard';
    }
    for (const pageId of ORDERED_USER_PAGE_IDS) {
      if (allowedPages.includes(pageId) && USER_PAGE_ID_TO_PATH[pageId]) {
        return USER_PAGE_ID_TO_PATH[pageId];
      }
    }
    if (allowedPages.includes('dashboard')) return '/user/dashboard';
    return '/admin';
  }, [allowedPages, loading, userRole]);

  const getFirstAllowedAdminPath = useCallback((): string => {
    const isFullAdminRole =
      Boolean(userRole?.name) && /full\s*admin/i.test(String(userRole?.name ?? ""));
    if (isFullAdminRole) return "/admin/dashboard";

    for (const pageId of allowedPages) {
      const path = ADMIN_PAGE_ID_TO_PATH[pageId] ?? ADMIN_PAGE_ID_TO_PATH[pageId.replace?.(/\//g, "_")];
      if (path) return path;
    }
    if (allowedPages.includes("admin_dashboard")) return "/admin/dashboard";
    return "/admin";
  }, [allowedPages]);

  const refreshRole = useCallback(async () => {
    // Clear cache and refetch
    localStorage.removeItem('userRole');
    localStorage.removeItem('userRoleId');
    
    if (!userId) return;
    
    setLoading(true);
    try {
      let roleIdToUse = userRoleId || 1;
      if (isAdminEmail(userEmail)) {
        const response = await admin.getUsers({
          search: userEmail,
          limit: 1
        });

        let fetchedUser = null;
        if (Array.isArray(response.data)) {
          fetchedUser = response.data[0];
        } else if (response.data?.users) {
          fetchedUser = response.data.users[0];
        } else if (response.data?.data) {
          fetchedUser = response.data.data[0];
        }

        roleIdToUse = fetchedUser?.roleId || roleIdToUse;
      }

      const roleResponse = await admin.getRoleById(roleIdToUse);
      const role = roleResponse.data;
      
      const roleData = {
        id: role.id,
        name: role.name,
        description: role.description,
        pages: role.pages || [],
      };
      
      localStorage.setItem('userRole', JSON.stringify(roleData));
      localStorage.setItem('userRoleId', userId.toString());
      
      setUserRole(roleData);
      setAllowedPages(role.pages || []);
      
      // Update userAtom with roleId
      setUser((prev) => (prev ? { ...prev, roleId: roleData.id } : prev));
    } catch (error) {
    } finally {
      setLoading(false);
    }
  }, [isAdminEmail, setUser, userEmail, userId, userRoleId]);

  const syncRole = useCallback(async () => {
    // Silently sync role in background without showing loading state
    if (!userId) return;
    // Avoid duplicate role fetch right after login/bootstrap.
    // Initial role resolution is handled by fetchUserRole effect.
    const cachedRole = localStorage.getItem('userRole');
    const cachedUserId = localStorage.getItem('userRoleId');
    if (!cachedRole || cachedUserId !== userId.toString()) return;
    
    try {
      let roleIdToUse = userRoleId || 1;
      if (isAdminEmail(userEmail)) {
        const response = await admin.getUsers({
          search: userEmail,
          limit: 1
        });

        let fetchedUser = null;
        if (Array.isArray(response.data)) {
          fetchedUser = response.data[0];
        } else if (response.data?.users) {
          fetchedUser = response.data.users[0];
        } else if (response.data?.data) {
          fetchedUser = response.data.data[0];
        }

        roleIdToUse = fetchedUser?.roleId || roleIdToUse;
      }

      const roleResponse = await admin.getRoleById(roleIdToUse);
      const role = roleResponse.data;
      
      const roleData = {
        id: role.id,
        name: role.name,
        description: role.description,
        pages: role.pages || [],
      };
      
      // Check if role has changed
      const currentRole = cachedRole ? JSON.parse(cachedRole) : null;
      
      // Only update if role has actually changed
      if (!currentRole || 
          JSON.stringify(currentRole.pages) !== JSON.stringify(roleData.pages) ||
          currentRole.id !== roleData.id) {
        
        localStorage.setItem('userRole', JSON.stringify(roleData));
        localStorage.setItem('userRoleId', userId.toString());
        
        setUserRole(roleData);
        setAllowedPages(roleData.pages || []);
        
        // Update userAtom with roleId
        setUser((prev) => (prev ? { ...prev, roleId: roleData.id } : prev));
      }
    } catch (error) {
      // Silently fail - don't disrupt user experience
    }
  }, [isAdminEmail, setUser, userEmail, userId, userRoleId]);

  return (
    <RoleContext.Provider value={{ userRole, allowedPages, loading, hasPageAccess, getFirstAllowedUserPath, getFirstAllowedAdminPath, refreshRole, syncRole }}>
      {children}
    </RoleContext.Provider>
  );
};

